import React, { useEffect, useImperativeHandle, useRef } from 'react';
import { combineClassNames } from '@common/utils/combineClassNames';
import { Message } from '../../message';

const parsePixelsToInt = (px: any) => parseInt(px, 10);

const getStyling = (target: HTMLTextAreaElement, keys: string[]) => keys
  .map((key) => {
    return window.getComputedStyle ?
      window.getComputedStyle(target).getPropertyValue(key) :
      // @ts-expect-error
      target.currentStyle[key];
  }).map(parsePixelsToInt);

const autoresize = (target: HTMLTextAreaElement) => {
  const { style } = target;
  const [paddingTop, paddingBottom, borderTopWidth, borderBottomWidth, minHeight] = getStyling(
    target,
    ['padding-top', 'padding-bottom', 'border-top-width', 'border-bottom-width', 'min-height'],
  );

  const paddingSize = paddingTop + paddingBottom;
  const borderSize = borderTopWidth + borderBottomWidth;

  if (!minHeight) style.minHeight = `${target.offsetHeight - paddingSize - borderSize}px`;

  // Reset height to prevent a growing input when typing
  style.height = '0';
  // TODO: Magic number (2) used to prevent having a scrollbar
  // Should be properly fixed in scope of ONE-1028
  style.height = `${target.scrollHeight - paddingSize - borderSize + 2}px`;
};

const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>, onChange: (newValue: string) => void, rows?: number) => {
  onChange(e.target.value);

  if (!rows) autoresize(e.target);
};

export type TextAreaInputProps = Omit<JSX.IntrinsicElements['textarea'], 'ref' | 'onChange'> & {
  warning?: string | boolean;
  error?: string | boolean;
  minRows?: number;
  disableNewLine?: boolean;
  disableValidationFeedback?: boolean;
  onEnter?: () => void;
  onPaste?: (event: React.ClipboardEvent<HTMLTextAreaElement>) => void;
  onChange: (newValue: string) => void;
  value?: string;
};

const TextAreaInput = React.forwardRef<HTMLTextAreaElement, TextAreaInputProps>(({
  rows,
  minRows,
  onChange,
  onEnter,
  disabled,
  disableNewLine,
  disableValidationFeedback = false,
  placeholder = '',
  className = '',
  error,
  warning,
  value,
  ...props
}, ref) => {
  const internalRef = useRef<HTMLTextAreaElement>(null);

  // ONE-2217 - Forward the ref to parent components
  useImperativeHandle(ref, () => internalRef.current as HTMLTextAreaElement);

  // ONE-2217 - Autoresize textarea on mount if a value exists
  useEffect(() => {
    if (internalRef.current && value && !rows) {
      autoresize(internalRef.current);
    }
  }, []);

  return (
    <>
      <textarea
        className={combineClassNames('Form__control', 'Form__control--multiLine', className, {
          'Form__control--warning': !!warning && !disableValidationFeedback,
          'Form__control--invalid': !warning && !!error && !disableValidationFeedback,
        })}
        disabled={disabled}
        ref={internalRef}
        placeholder={placeholder}
        value={value}
        onChange={(e) => handleChange(e, onChange, rows)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && disableNewLine) e.preventDefault();
          if (e.key === 'Enter' && !e.shiftKey && onEnter) {
            onEnter();
            e.preventDefault();
          }
        }}
        rows={rows || minRows || 1}
        {...props}
      />
      <Message error={error} warning={warning} />
    </>
  );
});

export default TextAreaInput;
